import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import PageWrapper from "../modules/PageWrapper";
import TableSkeletonLoader from "../../../components/Common/TableSkeletonLoader";
import EmptyTable from "../../../components/Common/EmptyTable";
import TablePagination from "../../../components/Table/Pagination";
import { useGetMemberEminutesQuery } from "../../../store/member/eminutesSlice";
import { dateFromString } from "../../../utils";
import { BsFileEarmarkText, BsArrowRight } from "react-icons/bs";
import moment from "moment";

export default function MemberEminutesList() {
  const navigate = useNavigate();
  const [pageNumber, setPageNumber] = useState(1);
  const { data = {}, isLoading, isFetching } = useGetMemberEminutesQuery({
    page: pageNumber,
    limit: 20,
  });

  const { eminutes = [], pagination = {} } = data;
  const formattedList = eminutes.map((item) => ({
    ...item,
    dateFormatted: item.created_at ? dateFromString(item.created_at) : "—",
    dateDisplay: item.created_at ? moment(item.created_at).format("DD MMM YYYY") : "—",
  }));

  const handleRead = (id) => {
    if (id) navigate(`/member/eminutes/${id}`);
  };

  return (
    <Wrapper>
      <PageWrapper title="E-Minutes">
        <div className="page-intro">
          <p className="text-muted mb-0">
            Official cooperative records and announcements. Click any item to read the full content.
          </p>
        </div>

        {isLoading || isFetching ? (
          <div className="eminutes-card">
            <TableSkeletonLoader rows={6} columns={1} />
          </div>
        ) : formattedList.length === 0 ? (
          <div className="eminutes-card empty-state" data-tour="member-eminutes-list">
            <EmptyTable Message="No E-Minutes available. Published minutes will appear here." />
          </div>
        ) : (
          <>
            <div className="eminutes-grid" data-tour="member-eminutes-list">
              {formattedList.map((item) => (
                <EminuteCard
                  key={item.id}
                  item={item}
                  onRead={() => handleRead(item.id)}
                />
              ))}
            </div>

            {(pagination?.totalPages ?? pagination?.total_pages) > 1 && (
              <div className="pagination-wrapper">
                <TablePagination
                  currentPage={pagination?.currentPage ?? pagination?.current_page ?? 1}
                  perPage={pagination?.perPage ?? pagination?.per_page ?? 20}
                  total={pagination?.total ?? 0}
                  totalPages={pagination?.totalPages ?? pagination?.total_pages ?? 1}
                  handlePageChange={setPageNumber}
                  marginTop="0"
                />
              </div>
            )}
          </>
        )}
      </PageWrapper>
    </Wrapper>
  );
}

function EminuteCard({ item, onRead }) {
  return (
    <Card
      onClick={onRead}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onRead();
        }
      }}
      role="button"
      tabIndex={0}
      data-tour="member-eminutes-open"
    >
      <div className="card-icon">
        <BsFileEarmarkText size={24} />
      </div>
      <div className="card-content">
        <h3 className="card-title">{item.title || "Untitled"}</h3>
        <span className="card-date">{item.dateDisplay}</span>
      </div>
      <div className="card-action">
        <span className="read-label">Read</span>
        <BsArrowRight size={18} className="arrow-icon" />
      </div>
    </Card>
  );
}

const Wrapper = styled.section`
  .page-intro {
    margin-bottom: 1.5rem;
    padding: 0 0.25rem;
  }

  .page-intro p {
    font-size: 14px;
    line-height: 1.5;
    color: #525252;
  }

  .eminutes-card {
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    border: 1px solid #e5e5e5;
    overflow: hidden;
  }

  .eminutes-card.empty-state {
    padding: 2rem;
  }

  .eminutes-grid {
    display: grid;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  .pagination-wrapper {
    padding: 1rem 0;
    display: flex;
    justify-content: center;
  }
`;

const Card = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  background: #ffffff;
  border: 1px solid #e5e5e5;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);

  &:hover {
    border-color: rgba(139, 49, 58, 0.3);
    box-shadow: 0 4px 12px rgba(139, 49, 58, 0.08);
    background: #fafafa;

    .card-action {
      color: var(--color-primary);

      .arrow-icon {
        transform: translateX(4px);
      }
    }
  }

  &:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 2px rgba(139, 49, 58, 0.15);
  }

  .card-icon {
    flex-shrink: 0;
    width: 48px;
    height: 48px;
    border-radius: 10px;
    background: linear-gradient(135deg, rgba(139, 49, 58, 0.08) 0%, rgba(49, 119, 140, 0.08) 100%);
    color: var(--color-primary);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .card-content {
    flex: 1;
    min-width: 0;
  }

  .card-title {
    font-size: 16px;
    font-weight: 600;
    color: #45464e;
    margin: 0 0 0.25rem 0;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .card-date {
    font-size: 13px;
    color: #525252;
    font-weight: 400;
  }

  .card-action {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: var(--color-secondary);
    font-size: 14px;
    font-weight: 600;
    transition: color 0.2s ease;

    .read-label {
      white-space: nowrap;
    }

    .arrow-icon {
      transition: transform 0.2s ease;
    }
  }
`;
